import { useSnackbar } from 'notistack';
import { useState, useEffect, useCallback } from 'react';

import { LoadingButton } from '@mui/lab';
import Grid from '@mui/system/Unstable_Grid/Grid';
import { MobileDatePicker } from '@mui/x-date-pickers';
import {
  Dialog,
  Select,
  Button,
  MenuItem,
  TextField,
  InputLabel,
  DialogTitle,
  FormControl,
  DialogContent,
  DialogActions,
} from '@mui/material';

import { useTranslate } from 'src/locales';
import { VisibilityRule } from 'src/services/jobs/jobs.types';
import { ICandidateProfileDocument } from 'src/services/candidates/candidates.types';
import { useUpdateProfileDocumentMutation } from 'src/services/candidates/candidates.service';

type Props = {
  open: boolean;
  onClose: VoidFunction;
  documentId: string;
  /**
   * The row being edited. Pre-fills the form so saving cannot quietly reset the visibility rule, and
   * decides whether the title field shows: only the organisation managing an application may retitle
   * its copy, and a signed document shows its title read-only.
   */
  row?: ICandidateProfileDocument;
};

export default function EditProfileDocumentModal({ open, onClose, documentId, row }: Props) {
  const { t } = useTranslate();

  const { enqueueSnackbar } = useSnackbar();

  const [updateDocument, { isLoading: isUpdatingDocument }] = useUpdateProfileDocumentMutation();

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const [selectedVisiblity, setSelectedVisiblity] = useState<VisibilityRule>(row?.visibility_rule ?? VisibilityRule.PUBLIC);

  const [title, setTitle] = useState(row?.display_name ?? '');

  const canRename = Boolean(row?.can_rename);

  const showTitle = canRename || Boolean(row?.is_signed);

  useEffect(() => {
    if (!open) return;

    setSelectedVisiblity(row?.visibility_rule ?? VisibilityRule.PUBLIC);
    setTitle(row?.display_name ?? '');
  }, [open, row?.visibility_rule, row?.display_name]);

  const handleUpdateDocument = useCallback(async () => {
    // Only a title the user changed is sent, so saving an expiry date never touches it.
    const titleChanged = canRename && title.trim() !== (row?.display_name ?? '');

    try {
      await updateDocument({
        documentId,
        data: {
          visibility_rule: selectedVisiblity,
          expiry_date: selectedDate?.toISOString(),
          ...(titleChanged ? { name: title.trim() || null } : {}),
        },
      }).unwrap();

      onClose();
      enqueueSnackbar(t('profile.api.edit_document.success'), { variant: 'success' });
    } catch (error) {
      console.error(error);

      enqueueSnackbar(t('profile.api.edit_document.default_error'), { variant: 'error' });
    }
  }, [canRename, documentId, enqueueSnackbar, onClose, row?.display_name, selectedDate, selectedVisiblity, t, title, updateDocument]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('profile.documents.edit_modal.title')}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} paddingTop={2}>
          {showTitle && (
            <Grid xs={12}>
              <TextField
                fullWidth
                label={t('profile.documents.title_field')}
                value={canRename ? title : (row?.document.name ?? '')}
                placeholder={row?.document.original_name ?? row?.document.name}
                onChange={(event) => setTitle(event.target.value)}
                disabled={!canRename}
                inputProps={{ maxLength: 255 }}
                helperText={canRename ? t('profile.documents.title_hint') : t('profile.documents.signed_hint')}
              />
            </Grid>
          )}

          <Grid xs={6}>
            <FormControl fullWidth sx={{ width: '100%' }}>
              <InputLabel id="doc-type-label">{t('profile.documents.visibility')}</InputLabel>
              <Select
                id="doc-type"
                labelId="doc-type-label"
                label={t('profile.documents.visibility')}
                value={selectedVisiblity}
                onChange={(e) => setSelectedVisiblity(e.target.value as VisibilityRule)}
                fullWidth
              >
                <MenuItem value={VisibilityRule.PUBLIC}>
                  {t('enums.visibility_rule.public')}
                </MenuItem>
                <MenuItem value={VisibilityRule.HIDDEN}>
                  {t('enums.visibility_rule.hidden')}
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid xs={6}>
            <MobileDatePicker
              label={t('profile.documents.expiry_date')}
              value={null}
              onChange={(value: Date | null) => {
                setSelectedDate(value);
              }}
              sx={{ width: '100%' }}
              slotProps={{
                actionBar: {
                  actions: ['clear', 'today', 'cancel', 'accept'],
                },
              }}
            />
          </Grid>

          <Grid xs={12}>
            <LoadingButton
              fullWidth
              variant="contained"
              loading={isUpdatingDocument}
              onClick={handleUpdateDocument}
            >
              {t('common.edit')}
            </LoadingButton>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
      </DialogActions>
    </Dialog>
  );
}
